import { useAtomValue } from 'jotai';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import {
  documentAtom,
  resourceSourceFromRoutedFrom,
  translatedDocumentNameAtom,
  useRoutedFrom,
} from '@/atoms/document';

/** How the user started a Tridion resource print. */
export type PrintTrigger = 'button' | 'keyboard' | 'browser';

/**
 * Returns a function that fires `resource_print` with document context and
 * `print_trigger` (`button` | `keyboard` | `browser`).
 */
export const useTrackResourcePrint = () => {
  const track = useAmplitudeTrack();
  const { document } = useAtomValue(documentAtom);
  const docName = useAtomValue(translatedDocumentNameAtom);
  const [routedFrom] = useRoutedFrom();

  const trackResourcePrint = (
    printTrigger: PrintTrigger,
    nameFallback?: string,
  ) => {
    // Preview / non-document surfaces mount Tridion UI without updating
    // documentAtom — skip rather than emit missing or stale document_id.
    if (!document?.id || document.sourceSystem !== 'TRIDION') {
      return;
    }

    track(AmplitudeProductEvent.RESOURCE_PRINT, {
      document_id: document.id,
      document_name: docName ?? nameFallback,
      source_system: 'TRIDION',
      source: resourceSourceFromRoutedFrom(routedFrom),
      is_native: window.native.isNative,
      print_trigger: printTrigger,
    });
  };

  return trackResourcePrint;
};
